import { getUserInfo } from 'app/store/session/repository';
import { platformLinkingSelector } from 'modules/platform-linking/store/selectors';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'shared/components/Modal';
import { withModalOpenChecker } from 'shared/HOC/withModalOpenChecker';
import { Alert, Box, Grid, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { ModalName } from 'app/enums';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { Controller, FormProvider } from 'react-hook-form';
import { useLinkAccountManuallyForm } from 'modules/platform-linking/hooks';
import { useCancelToken } from 'app/services/api/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { modalsReducers } from 'app/store/modal/slice';
import { connectEvIoUserAccount } from 'modules/platform-linking/api';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { TextInput } from 'shared/components/Form/TextInput';

const ModalComponent = () => {
  const navigate = useNavigate();
  const cancelToken = useCancelToken();
  const dispatch = useDispatch();
  const { form } = useLinkAccountManuallyForm();
  const {
    control,
    handleSubmit,
    formState: { isValid, isValidating, errors },
  } = form;
  const redirectPath = useSelector(platformLinkingSelector.internalRedirect);

  const [isLoading, setLoading] = useState(false);

  const onSubmit = useCallback(
    (data) => {
      setLoading(true);

      connectEvIoUserAccount(data, cancelToken).finally(() => {
        setLoading(false);
        dispatch(modalsReducers.closeModal(ModalName.PlatformLinkingManual));
        dispatch(getUserInfo());

        if (redirectPath) {
          navigate(redirectPath);
        }
      });
    },
    [cancelToken, dispatch, redirectPath, navigate]
  );

  return (
    <Modal
      maxWidth="md"
      header={<Box />}
      isHeaderHighlighted={false}
      isFooterHighlighted={false}
      name={ModalName.PlatformLinkingManual}
      disableEnforceFocus={true}
      contentHeight={220}
      actions={
        <LoaderButton
          fullWidth
          variant="contained"
          color="primary"
          disabled={!isValid || isValidating}
          loading={isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          Connect
        </LoaderButton>
      }
    >
      <Box px={2}>
        <Box mb={2}>
          <Alert
            severity="info"
            icon={
              <Box mt={1.75}>
                <InfoIcon />
              </Box>
            }
          >
            <Typography mb={1}>Please enter your Ev.io account ID below.</Typography>
            <Typography>
              You can find your account ID by visiting{' '}
              <a href="https://ev.io/user" target="_blank" rel="noreferrer">
                https://ev.io/user
              </a>{' '}
              after you have been signed in to Ev.io and copying numbers after the &quot;user/&quot;
              part of the page address.
            </Typography>
          </Alert>
        </Box>

        <FormProvider {...form}>
          <Grid container rowSpacing={1.5} columnSpacing={1}>
            <Grid item xs={4} sm={2} display="flex" alignItems="center">
              <Typography>Account ID</Typography>
            </Grid>
            <Grid item xs={8} sm={10}>
              <Controller
                control={control}
                name="accountId"
                render={({ field }) => <TextInput {...field} placeholder="E.g. 123" />}
              />
            </Grid>
            <Grid item>
              {errors?.accountId?.message ? (
                <ErrorMessage error={errors.accountId.message} />
              ) : null}
            </Grid>
          </Grid>
        </FormProvider>
      </Box>
    </Modal>
  );
};

export const PlatformLinkingManualModal = withModalOpenChecker(
  ModalComponent,
  ModalName.PlatformLinkingManual
);
